import React, { useRef, useState } from "react";
import bn from "../../utils/bemnames";
import {ICompetitionTournaments, IStandingRow, IStandingsPosition} from "../../api/match-center";
import { _t } from "../../utils/translations";
import ImgWithDefault from "../ImgWithDefault";
import {getDefaultCompetitorImage} from "../../utils/competitor";
import {getCompetitorLink} from "../../utils/url-structure";
import {IDeal} from "../../utils/types";
import {isGoalsDataAvailable} from "../../utils/standings";
import CountryFlag from "../CountryFlag";

const bem = bn.create("standings-table");

interface IProps {
  standingRows: IStandingRow[];
  highlightCompetitorIds: number[];
  oddsFormat: string;
  outLinkFolder: string | null;
  tournaments: ICompetitionTournaments[];
  tournamentStageId: number | string;
  tournamentStageName?: string;
  providerDeals: IDeal[];
  containerWidth: number;
  standingPositions: IStandingsPosition[];
}

const StandingsTable: React.FC<IProps> = (props) => {
  const {
    standingRows,
    highlightCompetitorIds,
    tournamentStageId,
    tournamentStageName,
    containerWidth,
  } = props

  const [showDetails] = useState<Boolean[]>(new Array(standingRows.length).fill(false))
  const [, getDimension] = useState({
    dynamicWidth: window.pageXOffset,
    dynamicHeight: window.pageYOffset,
  });
  const ref = useRef<any>(null);

  const setDimension = () => {
    getDimension({
      dynamicWidth: window.pageXOffset,
      dynamicHeight: window.pageYOffset,
    });
  };

  const isTablet = () => {
    return containerWidth < 1086;
  };

  const isMobile = () => {
    return containerWidth < 880;
  };

  const filterStandingRows = React.useCallback((): IStandingRow[] => {
    if (tournamentStageId === -1)
      return standingRows;

    return standingRows.filter((item) => item.tournamentStageId === tournamentStageId);
  }, [ tournamentStageId, standingRows ])

  React.useEffect(() => {
    window.addEventListener('resize', setDimension);

    return () => {
      window.removeEventListener("resize", setDimension);
    };
  });

  return (
    <>
      {standingRows.length > 0 && (
        <div className={`${bem.b()} ${isMobile() ? ' mobile' : (isTablet() ? ' tablet' : '')}`}>
          <div className={bem.e('wrapper')} ref={ref}>
            <div className={bem.e('table__row header_row')}>
              <div className={bem.e('table__cell__header__place')}>{tournamentStageName}</div>
              <div className={`${bem.e('table__cell')} ${bem.e('table__number')}`}>{_t('Pl')}</div>
              <div className={`${bem.e('table__cell')} ${bem.e('table__number')}`}>{_t('W')}</div>
              <div className={`${bem.e('table__cell')} ${bem.e('table__number')}`}>{_t('D')}</div>
              <div className={`${bem.e('table__cell')} ${bem.e('table__number')}`}>{_t('L')}</div>
              {!isTablet() && isGoalsDataAvailable(standingRows) && (
                <React.Fragment>
                  <div className={`${bem.e('table__cell')} ${bem.e('table__number')}`}>{_t("GF")}</div>
                  <div className={`${bem.e('table__cell')} ${bem.e('table__number')}`}>{_t("GA")}</div>
                  <div className={`${bem.e('table__cell')} ${bem.e('table__number')}`}>{_t("GD")}</div>
                </React.Fragment>
              )}
              <div className={`${bem.e('table__cell')} ${bem.e('table__number')}`}>
                {_t("Pts")}
              </div>
              {!isMobile() && <div className={bem.e("table__cell form_header")}>{_t("Form")}</div>}
            </div>
            {filterStandingRows().map((standingRow: IStandingRow, index) => {
              return (
                <React.Fragment key={index}>
                  <div
                    className={`${bem.e('table__row')} ${showDetails[index] ? 'detailed' : ''} ${isMobile() ? 'mobile' : ''}`}
                    style={{boxShadow: `inset 3px 0 0 ${standingRow.color}`}}
                  >
                    <div className={`${bem.e("table__cell__place")} ${bem.e('table__number')}`}>
                      <div className={`${bem.e('table__cell__place__status')}`}>
                        <span>{standingRow.rank}</span>
                        {standingRow.prevRank && standingRow.prevRank !== standingRow.rank ? (
                          <div
                            className={bem.e(`triangle ${standingRow.prevRank > standingRow.rank ? 'up' : 'down'}`)}></div>
                        ) : ''}
                      </div>
                    </div>
                    <div className={`${bem.e("table__cell")} ${bem.e("table__cell__competitor")} ${isMobile() ? 'mobile' : ''}`}>
                      {standingRow.competitor.logoUrl && (
                        <>
                          {standingRow.competitor.isNationalTeam && standingRow.competitor.region.name ? (
                            <CountryFlag
                              regionName={standingRow.competitor.region.name}
                            />
                          ) : (
                            <ImgWithDefault
                              alt={standingRow.competitor.trName}
                              src={standingRow.competitor.logoUrl}
                              defaultSrc={getDefaultCompetitorImage(standingRow.competitor.sportId)}
                            />
                          )}
                        </>
                      )}
                      <div className={`${bem.e("table__cell__competitor__name")} ${isMobile() ? 'mobile' : ''}`}>
                        <a
                          href={
                            getCompetitorLink(highlightCompetitorIds[0] === standingRow.competitor.id ? 1 : 2) ??
                            undefined
                          }
                        >
                          {standingRow.competitor.trName}
                        </a>
                      </div>
                    </div>
                    <div className={`${bem.e('table__cell')} ${bem.e('table__number')}`}>
                      {standingRow.played}
                    </div>
                    <div className={`${bem.e('table__cell')} ${bem.e('table__number')}`}>
                      {standingRow.wins}
                    </div>
                    <div className={`${bem.e('table__cell')} ${bem.e('table__number')}`}>
                      {standingRow.draws}
                    </div>
                    <div className={`${bem.e('table__cell')} ${bem.e('table__number')}`}>
                      {standingRow.losses}
                    </div>
                    {!isTablet() && isGoalsDataAvailable(standingRows) && (
                      <React.Fragment>
                        <div className={`${bem.e('table__cell')} ${bem.e('table__number')}`}>
                          {standingRow.goalsfor}
                        </div>
                        <div className={`${bem.e('table__cell')} ${bem.e('table__number')}`}>
                          {standingRow.goalsagainst}
                        </div>
                        <div className={`${bem.e('table__cell')} ${bem.e('table__number')}`}>
                          {standingRow.goalsdiff}
                        </div>
                      </React.Fragment>
                    )}
                    <div
                      className={`
                        ${bem.e('table__cell__points')}
                        ${bem.e('table__cell')}
                        ${bem.e('table__number')}
                      `}
                    >
                      {standingRow.points}
                    </div>
                    {!isMobile() && <div className={bem.e("details-table__row__cell content last-games")}>
                      {[...Array(5), ...(standingRow.lastResults??[])].slice(-5).map((item, index) => {
                        switch (item) {
                          case "win":
                            return <div key={'prevres'+index} className={"results-container win"}>{_t("W")}</div>;
                          case "draw":
                            return <div key={'prevres'+index} className={"results-container draw"}>{_t("D")}</div>;
                          case "loss":
                            return <div key={'prevres'+index} className={"results-container lose"}>{_t("L")}</div>;
                          default:
                            return <div key={'prevres'+index} className={"results-container unknown"}>?</div>;
                        }
                      })}
                    </div>}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default StandingsTable;
